import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataset,
} from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import { isEqual } from "lodash";

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

// Colors for loops
const LOOP_COLORS = [
  "#E6194B",
  "#3CB44B",
  "#FFE119",
  "#4363D8",
  "#F58231",
  "#BB66FF",
  "#46F0F0",
  "#FF66FF",
];

interface LoopTrackMapProps {
  useCombinedLoops?: boolean;
}

export default function LoopTrackMap({
  useCombinedLoops = true,
}: LoopTrackMapProps) {
  const trackMap = useSelector(
    (state: RootState) => state.trackMap?.trackMap,
    isEqual,
  );

  const timingLoops = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.track?.info?.timingLoops,
    isEqual,
  );

  const strategyImport = useSelector(
    (state: RootState) => state.imports.imports.strategy?.data,
    isEqual,
  );

  const trackMapRotation = strategyImport?.trackMapRotation ?? 0;

  // Track the container's actual pixel size so the chart's x/y scales can be matched to it
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Measure synchronously before paint so the first render already has the right aspect ratio
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setContainerSize({ width: rect.width, height: rect.height });
    }

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setContainerSize({ width, height });
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Build loop configuration
  const loopConfig = useMemo(() => {
    if (!timingLoops) return { loopNames: [], combinedLoops: [] };

    // Get all loop indices sorted (loop 0 = FL goes last)
    const loopIndices = Object.keys(timingLoops)
      .map((k) => parseInt(k, 10))
      .filter((n) => !isNaN(n))
      .sort((a, b) => {
        if (a === 0) return 1;
        if (b === 0) return -1;
        return a - b;
      });

    const loopNames = loopIndices.map((idx) => {
      const loopInfo = timingLoops?.[`${idx}` as `${number}`];
      return loopInfo?.name || `L${idx}`;
    });

    // Get combined loop configuration from strategy import
    const combinedLoops: { label: string; loopNames: string[] }[] = [];
    for (let i = 1; i <= 8; i++) {
      const loopNamesArray = strategyImport?.[`combinedLoop${i}`] as
        | (string | null)[]
        | undefined;
      const label = strategyImport?.[`combinedLoopLabel${i}`] as
        | string
        | undefined;

      if (label && loopNamesArray) {
        const validLoopNames = loopNamesArray.filter(
          (name): name is string => name !== null && name !== undefined,
        );
        if (validLoopNames.length > 0) {
          combinedLoops.push({ label, loopNames: validLoopNames });
        }
      }
    }

    return { loopNames, loopIndices, combinedLoops };
  }, [timingLoops, strategyImport]);

  // Map loop names to their marking positions
  const loopPositions = useMemo(() => {
    if (!trackMap?.loopMarkings) return {};

    const positions: Record<string, { lat: number; lon: number }> = {};
    trackMap.loopMarkings.forEach((loop) => {
      positions[loop.name] = { lat: loop.lat, lon: loop.lon };
    });
    return positions;
  }, [trackMap]);

  // Rotate lon/lat points clockwise by trackMapRotation degrees around the track's geometric center
  const rotatePoint = useMemo(() => {
    if (!trackMap?.trackEdges?.inner?.lat?.length || !trackMapRotation) {
      return (lon: number, lat: number) => ({ x: lon, y: lat });
    }

    const allLats = [
      ...trackMap.trackEdges.inner.lat,
      ...(trackMap.trackEdges.outer?.lat || []),
    ];
    const allLons = [
      ...trackMap.trackEdges.inner.lon,
      ...(trackMap.trackEdges.outer?.lon || []),
    ];
    const centerLat = (Math.min(...allLats) + Math.max(...allLats)) / 2;
    const centerLon = (Math.min(...allLons) + Math.max(...allLons)) / 2;
    const cosLat = Math.cos((centerLat * Math.PI) / 180);

    // Clockwise rotation (screen space has y increasing upward, same as latitude)
    const theta = (trackMapRotation * Math.PI) / 180;
    const cosTheta = Math.cos(theta);
    const sinTheta = Math.sin(theta);

    return (lon: number, lat: number) => {
      const localX = (lon - centerLon) * cosLat;
      const localY = lat - centerLat;
      const rotatedX = localX * cosTheta + localY * sinTheta;
      const rotatedY = localY * cosTheta - localX * sinTheta;
      return {
        x: centerLon + rotatedX / cosLat,
        y: centerLat + rotatedY,
      };
    };
  }, [trackMap, trackMapRotation]);

  // Find the closest point on track to a loop marking
  const findClosestTrackPoint = (
    loopPos: { lat: number; lon: number },
    trackLats: number[],
    trackLons: number[],
  ): number => {
    let minDist = Infinity;
    let closestIdx = 0;

    for (let i = 0; i < trackLats.length; i++) {
      const dist =
        Math.pow(trackLats[i] - loopPos.lat, 2) +
        Math.pow(trackLons[i] - loopPos.lon, 2);
      if (dist < minDist) {
        minDist = dist;
        closestIdx = i;
      }
    }
    return closestIdx;
  };

  // Build track segments colored by loop
  const { trackDatasets, legendItems } = useMemo(() => {
    const datasets: ChartDataset<"scatter">[] = [];
    const legend: { label: string; color: string }[] = [];

    if (!trackMap?.trackEdges?.inner?.lat?.length)
      return { trackDatasets: datasets, legendItems: legend };

    const innerLats = trackMap.trackEdges.inner.lat;
    const innerLons = trackMap.trackEdges.inner.lon;
    const outerLats = trackMap.trackEdges.outer.lat;
    const outerLons = trackMap.trackEdges.outer.lon;

    // Find indices where each loop intersects the track
    const loopIndicesOnTrack: {
      name: string;
      innerIdx: number;
      outerIdx: number;
    }[] = [];

    loopConfig.loopNames.forEach((loopName) => {
      const pos = loopPositions[loopName];
      if (pos) {
        const innerIdx = findClosestTrackPoint(pos, innerLats, innerLons);
        const outerIdx = findClosestTrackPoint(pos, outerLats, outerLons);
        loopIndicesOnTrack.push({ name: loopName, innerIdx, outerIdx });
      }
    });

    // Sort by track position (inner index)
    loopIndicesOnTrack.sort((a, b) => a.innerIdx - b.innerIdx);

    // Determine color for each loop based on combined loops
    const loopToColor: Record<string, string> = {};
    const shouldUseCombined =
      useCombinedLoops && loopConfig.combinedLoops.length > 0;

    if (shouldUseCombined) {
      loopConfig.combinedLoops.forEach((combined, idx) => {
        const color = LOOP_COLORS[idx % LOOP_COLORS.length];
        combined.loopNames.forEach((loopName) => {
          loopToColor[loopName] = color;
        });
        legend.push({ label: combined.label, color });
      });
    } else {
      loopConfig.loopNames.forEach((loopName, idx) => {
        const color = LOOP_COLORS[idx % LOOP_COLORS.length];
        loopToColor[loopName] = color;
        legend.push({ label: loopName, color });
      });
    }

    // Create segments between consecutive loops
    for (let i = 0; i < loopIndicesOnTrack.length; i++) {
      const current = loopIndicesOnTrack[i];
      const next = loopIndicesOnTrack[(i + 1) % loopIndicesOnTrack.length];
      // Color segment by destination loop (next) since combined loops measure TO that point
      const color = loopToColor[next.name] || "#ffffff";

      // Handle wrap-around for the last segment
      const innerStart = current.innerIdx;
      const innerEnd = next.innerIdx;
      const outerStart = current.outerIdx;
      const outerEnd = next.outerIdx;

      // Inner edge segment
      const innerSegment: { x: number; y: number }[] = [];
      if (innerEnd > innerStart) {
        for (let j = innerStart; j <= innerEnd; j++) {
          innerSegment.push(rotatePoint(innerLons[j], innerLats[j]));
        }
      } else {
        // Wrap around
        for (let j = innerStart; j < innerLats.length; j++) {
          innerSegment.push(rotatePoint(innerLons[j], innerLats[j]));
        }
        for (let j = 0; j <= innerEnd; j++) {
          innerSegment.push(rotatePoint(innerLons[j], innerLats[j]));
        }
      }

      // Outer edge segment
      const outerSegment: { x: number; y: number }[] = [];
      if (outerEnd > outerStart) {
        for (let j = outerStart; j <= outerEnd; j++) {
          outerSegment.push(rotatePoint(outerLons[j], outerLats[j]));
        }
      } else {
        // Wrap around
        for (let j = outerStart; j < outerLats.length; j++) {
          outerSegment.push(rotatePoint(outerLons[j], outerLats[j]));
        }
        for (let j = 0; j <= outerEnd; j++) {
          outerSegment.push(rotatePoint(outerLons[j], outerLats[j]));
        }
      }

      // Add inner segment
      datasets.push({
        data: innerSegment,
        borderColor: color,
        backgroundColor: "transparent",
        pointRadius: 0,
        showLine: true,
        borderWidth: 3,
        fill: false,
        order: 100,
      });

      // Add outer segment
      datasets.push({
        data: outerSegment,
        borderColor: color,
        backgroundColor: "transparent",
        pointRadius: 0,
        showLine: true,
        borderWidth: 3,
        fill: false,
        order: 100,
      });
    }

    // Add timing lines (white)
    trackMap.lines?.forEach((line) => {
      const lineData = line.lat.map((lat, i) => rotatePoint(line.lon[i], lat));
      datasets.push({
        label: line.name,
        data: lineData,
        borderColor: "#ffffff",
        backgroundColor: "transparent",
        pointRadius: 0,
        showLine: true,
        borderWidth: 2,
        fill: false,
        order: 50,
      });
    });

    // Add turn markings
    trackMap.turnMarkings?.forEach((turn) => {
      const turnLabel = turn.name.startsWith("T") ? turn.name : `T${turn.name}`;
      datasets.push({
        label: turnLabel,
        data: [rotatePoint(turn.lon, turn.lat)],
        pointRadius: 0,
        backgroundColor: "transparent",
        borderColor: "transparent",
        showLine: false,
        order: 80,
      });
    });

    // Add loop markings with labels
    trackMap.loopMarkings?.forEach((loop) => {
      datasets.push({
        label: loop.name,
        data: [rotatePoint(loop.lon, loop.lat)],
        pointRadius: 4,
        backgroundColor: loopToColor[loop.name] || "#ffffff",
        borderColor: "#ffffff",
        pointBorderWidth: 1,
        showLine: false,
        order: 70,
      });
    });

    return { trackDatasets: datasets, legendItems: legend };
  }, [trackMap, loopConfig, loopPositions, useCombinedLoops, rotatePoint]);

  // Calculate bounds with cosine correction, fitted to the container's actual pixel
  // aspect ratio so a degree of lon/lat covers the same number of pixels on both axes
  const bounds = useMemo(() => {
    const aspect =
      containerSize && containerSize.height > 0
        ? containerSize.width / containerSize.height
        : 1;

    if (!trackMap?.trackEdges?.inner?.lat?.length) {
      return {
        minLon: 0,
        maxLon: Math.max(aspect, 1),
        minLat: 0,
        maxLat: Math.max(1 / aspect, 1),
      };
    }

    const allLats = [
      ...(trackMap.trackEdges.inner?.lat || []),
      ...(trackMap.trackEdges.outer?.lat || []),
    ];
    const allLons = [
      ...(trackMap.trackEdges.inner?.lon || []),
      ...(trackMap.trackEdges.outer?.lon || []),
    ];

    const rotatedPoints = allLats.map((lat, i) => rotatePoint(allLons[i], lat));
    const minLon = Math.min(...rotatedPoints.map((p) => p.x));
    const maxLon = Math.max(...rotatedPoints.map((p) => p.x));
    const minLat = Math.min(...rotatedPoints.map((p) => p.y));
    const maxLat = Math.max(...rotatedPoints.map((p) => p.y));

    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;

    const cosLat = Math.cos((centerLat * Math.PI) / 180);
    const lonRangeAdjusted = (maxLon - minLon) * cosLat;
    const latRange = maxLat - minLat;

    // Grow whichever axis has spare room so the track's bounding box is fully
    // contained within a box that matches the container's aspect ratio
    let finalLonRangeAdjusted: number;
    let finalLatRange: number;
    if (lonRangeAdjusted / aspect >= latRange) {
      finalLonRangeAdjusted = lonRangeAdjusted;
      finalLatRange = lonRangeAdjusted / aspect;
    } else {
      finalLatRange = latRange;
      finalLonRangeAdjusted = latRange * aspect;
    }

    const padding = Math.max(finalLonRangeAdjusted, finalLatRange) * 0.05;
    finalLonRangeAdjusted += 2 * padding;
    finalLatRange += 2 * padding;

    const finalLonRange = finalLonRangeAdjusted / cosLat;

    return {
      minLon: centerLon - finalLonRange / 2,
      maxLon: centerLon + finalLonRange / 2,
      minLat: centerLat - finalLatRange / 2,
      maxLat: centerLat + finalLatRange / 2,
    };
  }, [trackMap, containerSize, rotatePoint]);

  const chartData = useMemo(() => {
    return { datasets: trackDatasets };
  }, [trackDatasets]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      events: [],
      plugins: {
        datalabels: {
          display: (context: Context) => {
            return context.dataset.data.length === 1;
          },
          color: "#ffffff",
          backgroundColor: "transparent",
          textStrokeColor: "#2c2c2c",
          textStrokeWidth: 3,
          font: {
            size: 10,
            weight: "bold" as const,
          },
          formatter: (_value: unknown, context: Context) => {
            return context.dataset.label || "";
          },
          anchor: "center" as const,
          align: "center" as const,
        },
        legend: {
          display: legendItems.length > 0,
          position: "bottom" as const,
          onClick: () => undefined,
          labels: {
            color: "#ffffff",
            generateLabels: () => {
              return legendItems.map((item) => ({
                text: item.label,
                fillStyle: item.color,
                strokeStyle: item.color,
                fontColor: "#ffffff",
                lineWidth: 0,
              }));
            },
          },
        },
        title: { display: false },
        tooltip: { enabled: false },
      },
      scales: {
        x: {
          type: "linear" as const,
          min: bounds.minLon,
          max: bounds.maxLon,
          title: { display: false },
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
        },
        y: {
          type: "linear" as const,
          min: bounds.minLat,
          max: bounds.maxLat,
          title: { display: false },
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
        },
      },
    }),
    [bounds, legendItems],
  );

  if (!trackMap) {
    return (
      <div className={styles.container}>
        <div className={styles.noData}>No track map data</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.chartWrapper} ref={containerRef}>
        <Scatter data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}
